import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Slider,
    Alert,
} from '@mui/material';
import {
    Agriculture,
    Warning,
    CardGiftcard,
    DeleteForever,
    ArrowBack,
    BugReport,
    WaterDrop,
    AcUnit,
    Opacity,
    HelpOutline,
    Pets,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Plant, PlantRemovalAction, PlantKillReason } from '../../types/shared';

interface PlantRemovalDialogProps {
    open: boolean;
    plant: Plant | null;
    onClose: () => void;
    onConfirm: (data: { action: PlantRemovalAction; reason: string | null; count: number }) => void;
}

interface Action {
    id: PlantRemovalAction;
    icon: React.ElementType;
    color: string;
}

interface KillReason {
    id: PlantKillReason;
    icon: React.ElementType;
}

const ACTIONS: Action[] = [
    { id: 'harvested', icon: Agriculture, color: 'success.main' },
    { id: 'killed', icon: Warning, color: 'error.main' },
    { id: 'gifted', icon: CardGiftcard, color: 'info.main' },
    { id: 'delete', icon: DeleteForever, color: 'grey.600' },
];

const KILL_REASONS: KillReason[] = [
    { id: 'disease', icon: BugReport },
    { id: 'slugs', icon: Pets },
    { id: 'drought', icon: WaterDrop },
    { id: 'frost', icon: AcUnit },
    { id: 'overwatering', icon: Opacity },
    { id: 'unknown', icon: HelpOutline },
];

const PlantRemovalDialog: React.FC<PlantRemovalDialogProps> = ({ open, plant, onClose, onConfirm }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState('action'); // 'action', 'reason', 'confirm'
    const [selectedAction, setSelectedAction] = useState<Action | null>(null);
    const [selectedReason, setSelectedReason] = useState<KillReason | null>(null);
    const [count, setCount] = useState(1);
    const [giftRecipient, setGiftRecipient] = useState('');

    const handleClose = () => {
        // Reset state on close
        setStep('action');
        setSelectedAction(null);
        setSelectedReason(null);
        setCount(1);
        setGiftRecipient('');
        onClose();
    };

    const handleActionSelect = (action: Action) => {
        setSelectedAction(action);
        if (action.id === 'killed') {
            setStep('reason');
        } else {
            // Initialize count to total when entering confirm
            if (plant && (plant.count || 1) > 1 && action.id !== 'delete') {
                setCount(plant.count || 1);
            }
            setStep('confirm');
        }
    };

    const handleReasonSelect = (reason: KillReason) => {
        setSelectedReason(reason);
        // Initialize count to total when entering confirm
        if (plant && (plant.count || 1) > 1) {
            setCount(plant.count || 1);
        }
        setStep('confirm');
    };

    const handleBack = () => {
        if (step === 'confirm') {
            if (selectedAction?.id === 'killed') {
                setStep('reason');
            } else {
                setStep('action');
            }
        } else if (step === 'reason') {
            setStep('action');
        }
    };

    const handleConfirm = () => {
        if (!selectedAction) return;

        onConfirm({
            action: selectedAction.id,
            reason: selectedReason?.id || giftRecipient || null,
            count: (selectedAction.id === 'delete' && plant) ? (plant.count || 1) : count,
        });
        handleClose();
    };

    const getConfirmationMessage = () => {
        if (!selectedAction) return '';

        const plantName = plant?.name || 'this plant';
        const countText = count > 1 ? `${count} ` : '';
        const countName = `${countText}${plantName}`;

        switch (selectedAction.id) {
            case 'harvested':
                return t('plants.confirm_harvest', { countName });
            case 'killed':
                const reasonKey = `plants.reason_${selectedReason?.id || 'unknown'}`;
                return t('plants.confirm_loss', { countName, reason: t(reasonKey).toLowerCase() });
            case 'gifted':
                const recipient = giftRecipient ? t('plants.to_recipient', { name: giftRecipient }) : '';
                return t('plants.confirm_gift', { countName, recipient });
            case 'delete':
                return null; // Special handling for delete
            default:
                return '';
        }
    };

    if (!plant) return null;

    const showCountSlider = step === 'confirm' && (plant.count || 1) > 1 && selectedAction?.id !== 'delete';
    const showGiftRecipient = step === 'confirm' && selectedAction?.id === 'gifted';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {step !== 'action' && (
                    <IconButton size="small" onClick={handleBack} sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Box sx={{ flex: 1 }}>
                    {step === 'action' && t('plants.remove_plant_title', { name: plant.name })}
                    {step === 'reason' && t('plants.what_happened_title')}
                    {step === 'confirm' && (selectedAction?.id === 'delete' ? t('plants.confirm_delete_title') : t('plants.confirm_title'))}
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Step 1: Action Selection */}
                {step === 'action' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {ACTIONS.map((action) => {
                            const Icon = action.icon;
                            return (
                                <Button
                                    key={action.id}
                                    variant="outlined"
                                    onClick={() => handleActionSelect(action)}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        py: 1.5,
                                        px: 2,
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: action.color,
                                            bgcolor: `${action.color}15`,
                                        },
                                    }}
                                >
                                    <Icon sx={{ mr: 2, color: action.color }} />
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'none' }}>
                                            {t(`plants.action_${action.id}`)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'none' }}>
                                            {t(`plants.action_${action.id}_desc`)}
                                        </Typography>
                                    </Box>
                                </Button>
                            );
                        })}
                    </Box>
                )}

                {/* Step 2: Kill Reason Selection */}
                {step === 'reason' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                        {KILL_REASONS.map((reason) => {
                            const Icon = reason.icon;
                            return (
                                <Button
                                    key={reason.id}
                                    variant="outlined"
                                    onClick={() => handleReasonSelect(reason)}
                                    sx={{
                                        flexDirection: 'column',
                                        py: 2,
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: 'error.main',
                                            bgcolor: 'error.50',
                                        },
                                    }}
                                >
                                    <Icon sx={{ mb: 0.5, color: 'error.main' }} />
                                    <Typography variant="body2" sx={{ textTransform: 'none' }}>
                                        {t(`plants.reason_${reason.id}`)}
                                    </Typography>
                                </Button>
                            );
                        })}
                    </Box>
                )}

                {/* Step 3: Confirmation (with optional count and gift recipient) */}
                {step === 'confirm' && (
                    <Box sx={{ py: 1 }}>
                        {selectedAction?.id === 'delete' ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    {t('plants.delete_warning')}
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>{t('plants.delete_plant_name', { name: plant.name })}</li>
                                    <li>{t('plants.delete_associated_logs')}</li>
                                </ul>
                                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                                    {t('plants.cannot_be_undone')}
                                </Typography>
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {getConfirmationMessage()}
                                </Typography>

                                {/* Count slider for plants with count > 1 */}
                                {showCountSlider && (
                                    <Box sx={{ px: 1, mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {t('plants.how_many_plants', { count, total: plant.count || 1 })}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <Slider
                                                value={count}
                                                onChange={(e, value) => setCount(value as number)}
                                                min={1}
                                                max={plant.count || 1}
                                                step={1}
                                                marks
                                                valueLabelDisplay="auto"
                                            />
                                        </Box>
                                        {count < (plant.count || 1) && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t('plants.plants_remain', { count: (plant.count || 1) - count })}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Gift recipient field */}
                                {showGiftRecipient && (
                                    <TextField
                                        label={t('plants.recipient_label')}
                                        fullWidth
                                        value={giftRecipient}
                                        onChange={(e) => setGiftRecipient(e.target.value)}
                                        placeholder={t('plants.recipient_placeholder')}
                                        size="small"
                                    />
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                {step === 'confirm' && (
                    <Button
                        variant="contained"
                        color={selectedAction?.id === 'delete' ? 'error' : 'primary'}
                        onClick={handleConfirm}
                    >
                        {selectedAction?.id === 'delete' ? t('plants.delete_forever_btn') : t('plants.confirm_btn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PlantRemovalDialog;
